import { SpecialSessionRequest } from "./../../interfaces/special-sessions/SpecialSessionRequest";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { SpecialSessionDetailsResponse } from "../../interfaces/special-sessions/SpecialSessionDetailsResponse";
import { SpecialSessionResponse } from "../../interfaces/special-sessions/SpecialSessionResponse";
import { ConfirmSpecialSessionDeletionResponse } from "../../interfaces/special-sessions/ConfirmSpecialSessionDeletionResponse";
import { SetIsFeaturedOnParticipantsListRequest } from "../../interfaces/special-sessions/SetIsFeaturedOnParticipantsListRequest";
import { AN_ERROR_OCCURRED_MESSAGE } from "../../utils/constants";
import specialSessionsService from "../../services/specialSessionsService";

interface SpecialSessionsState {
  specialSessions: SpecialSessionResponse[];
  specialSessionsWithDeleted: SpecialSessionDetailsResponse[];
  loading: boolean;
  error: string | null;
}

const specialSessionsInitialState: SpecialSessionsState = {
  specialSessions: [],
  specialSessionsWithDeleted: [],
  loading: false,
  error: null,
};

export const fetchSpecialSessions = createAsyncThunk<
  SpecialSessionResponse[],
  void,
  { rejectValue: string }
>("specialSessions/fetchAll", async (_, { rejectWithValue }) => {
  try {
    const allSpecialSessions = await specialSessionsService.getAllSpecialSessions();
    return allSpecialSessions;
  } catch (error: any) {
    return rejectWithValue(error.message || AN_ERROR_OCCURRED_MESSAGE);
  }
});

export const fetchSpecialSessionsWithDeleted = createAsyncThunk<
  SpecialSessionDetailsResponse[],
  void,
  { rejectValue: string }
>("specialSessions/fetchAllWithDeleted", async (_, { rejectWithValue }) => {
  try {
    const allSpecialSessionsWithDeleted = await specialSessionsService.getAllSpecialSessionsWithDeleted();
    return allSpecialSessionsWithDeleted;
  } catch (error: any) {
    return rejectWithValue(error.message || AN_ERROR_OCCURRED_MESSAGE);
  }
});

export const createSpecialSession = createAsyncThunk<
  AxiosResponse<any, any>,
  SpecialSessionRequest,
  { rejectValue: string }
>(
  "specialSessions/create",
  async (specialSessionToCreate, { rejectWithValue }) => {
    try {
      const createdSpecialSession = await specialSessionsService.createSpecialSession(specialSessionToCreate);
      return createdSpecialSession.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message || AN_ERROR_OCCURRED_MESSAGE);
    }
  }
);

export const updateSpecialSession = createAsyncThunk<
  SpecialSessionResponse,
  { id: string; specialSessionToUpdate: SpecialSessionRequest },
  { rejectValue: string }
>(
  "specialSessions/update",
  async ({ id, specialSessionToUpdate }, { rejectWithValue }) => {
    try {
      const updatedSpecialSession = await specialSessionsService.updateSpecialSession(id, specialSessionToUpdate);
      return updatedSpecialSession;
    } catch (error: any) {
      return rejectWithValue(error.message || "");
    }
  }
);

export const deleteSpecialSession = createAsyncThunk<
  SpecialSessionDetailsResponse,
  string,
  { rejectValue: string }
>("specialSessions/delete", async (id, { rejectWithValue }) => {
  try {
    const deletedSpecialSession = await specialSessionsService.deleteSpecialSession(id);
    return deletedSpecialSession;
  } catch (error: any) {
    return rejectWithValue(error.message || AN_ERROR_OCCURRED_MESSAGE);
  }
});

export const confirmSpecialSessionDeletion = createAsyncThunk<
  ConfirmSpecialSessionDeletionResponse,
  string,
  { rejectValue: string }
>("specialSessions/confirm-deletion", async (id, { rejectWithValue }) => {
  try {
    const hardDeletedSpecialSession = await specialSessionsService.confirmSpecialSessionDeletion(id);
    return hardDeletedSpecialSession;
  } catch (error: any) {
    return rejectWithValue(error.message || AN_ERROR_OCCURRED_MESSAGE);
  }
});

export const restoreSpecialSession = createAsyncThunk<
  SpecialSessionDetailsResponse,
  string,
  { rejectValue: string }
>("specialSessions/restore", async (id, { rejectWithValue }) => {
  try {
    const restoredSpecialSession = await specialSessionsService.restoreSpecialSession(id);
    return restoredSpecialSession;
  } catch (error: any) {
    return rejectWithValue(error.message || AN_ERROR_OCCURRED_MESSAGE);
  }
});

export const setIsFeaturedOnParticipantsList = createAsyncThunk<
  SpecialSessionResponse,
  {
    id: string;
    setIsFeaturedOnParticipantsListRequest: SetIsFeaturedOnParticipantsListRequest;
  },
  { rejectValue: string }
>(
  "specialSessions/set-is-featured-on-participants-list",
  async (
    { id, setIsFeaturedOnParticipantsListRequest },
    { rejectWithValue }
  ) => {
    try {
      const specialSessionFeaturedOnParticipantsList = await specialSessionsService.setIsFeaturedOnParticipantsList(
        id, setIsFeaturedOnParticipantsListRequest
      );
      return specialSessionFeaturedOnParticipantsList;
    } catch (error: any) {
      return rejectWithValue(error.message || "");
    }
  }
);

const specialSessionsSlice = createSlice({
  name: "specialSessions",
  initialState: specialSessionsInitialState,
  reducers: {
    clearSpecialSessionsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (actionReducerMapBuilder) => {
    actionReducerMapBuilder
      .addCase(fetchSpecialSessions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSpecialSessions.fulfilled, (state, action) => {
        state.specialSessions = action.payload;
        state.loading = false;
      })
      .addCase(fetchSpecialSessions.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })
      .addCase(fetchSpecialSessionsWithDeleted.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSpecialSessionsWithDeleted.fulfilled, (state, action) => {
        state.specialSessionsWithDeleted = action.payload;
        state.loading = false;
      })
      .addCase(fetchSpecialSessionsWithDeleted.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })
      .addCase(createSpecialSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSpecialSession.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createSpecialSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteSpecialSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSpecialSession.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteSpecialSession.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })
      .addCase(confirmSpecialSessionDeletion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmSpecialSessionDeletion.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(confirmSpecialSessionDeletion.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })
      .addCase(restoreSpecialSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreSpecialSession.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(restoreSpecialSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(setIsFeaturedOnParticipantsList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setIsFeaturedOnParticipantsList.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(setIsFeaturedOnParticipantsList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearSpecialSessionsError } = specialSessionsSlice.actions;

export default specialSessionsSlice.reducer;